import { EventLocationData, Undefinable } from '@eagle/react-common';
import { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { formatTotalDistanceLabel, formatTotalFuelLabel, formatTripDurationLabel, getThingColorMap, getThingRangeBoundaryEvents } from './trip-history-base-view-utils';
import { ThingCustomRange, ThingRange, TripBounds, TripGroup, TripMetrics, TripSortOrder, TripSummary } from './trip-history-base-view.types';

export const getThingColorMapTyped = <T extends { _id: string }>(
  entities: T[],
  currentColorById: Record<string, string> = {},
  thingColors: string[] = [],
): Record<string, string> => {
  return getThingColorMap(entities, currentColorById, thingColors);
};

export const getThingRangeBoundaryEventsTyped = (events: EventLocationData[]): {
  end?: EventLocationData;
  start?: EventLocationData;
} => {
  return getThingRangeBoundaryEvents(events);
};

export const getPresetThingRangeBounds = (range: Exclude<ThingRange, 'custom'>, now: DateTime): TripBounds => {
  const startOfToday = now.startOf('day');
  const startOfWeek = now.startOf('week');

  switch (range) {
    case 'today':
      return { end: startOfToday.endOf('day'), start: startOfToday };
    case 'yesterday': {
      const yesterdayStart = startOfToday.minus({ days: 1 });
      return { end: yesterdayStart.endOf('day'), start: yesterdayStart };
    }
    case 'this-week':
      return { end: now, start: startOfWeek };
    case 'last-week':
      return { end: startOfWeek.minus({ milliseconds: 1 }), start: startOfWeek.minus({ weeks: 1 }) };
  }
};

export const normalizeThingCustomRange = (range: ThingCustomRange): ThingCustomRange => {
  if (!range.start || !range.end) return range;
  if (range.start.toMillis() <= range.end.toMillis()) return range;
  return {
    end: range.start,
    start: range.end,
  };
};

export const toThingRangeBounds = (range: ThingRange, customRange: Undefinable<ThingCustomRange>, now: DateTime): Undefinable<TripBounds> => {
  if (range === 'custom') {
    if (!customRange?.start || !customRange.end) return;
    const normalizedRange = normalizeThingCustomRange(customRange);
    if (!normalizedRange.start || !normalizedRange.end) return;
    return {
      end: normalizedRange.end,
      start: normalizedRange.start,
    };
  }

  return getPresetThingRangeBounds(range, now);
};

export const formatTripDuration = (start: DateTime, finish: DateTime): string => formatTripDurationLabel(start, finish);

export const formatTripGroupDayLabel = (day: DateTime, t: TFunction): string => {
  const today = DateTime.now().startOf('day');
  const yesterday = today.minus({ days: 1 });

  if (day.hasSame(today, 'day')) {
    return t('common:component.date-time-range.labels.today');
  }

  if (day.hasSame(yesterday, 'day')) {
    return t('common:component.date-time-range.labels.yesterday');
  }

  return day.toFormat('dd LLL, yyyy');
};

export const getTripGroups = (trips: TripSummary[], sortOrder: TripSortOrder): TripGroup[] => {
  const groups = new Map<string, TripGroup>();

  trips.forEach((trip) => {
    const day = trip.start.startOf('day');
    const key = day.toISODate() || day.toFormat('yyyy-LL-dd');
    const currentGroup = groups.get(key);

    if (currentGroup) {
      currentGroup.trips.push(trip);
      return;
    }

    groups.set(key, { day, key, trips: [trip] });
  });

  const orderedGroups = Array.from(groups.values());
  orderedGroups.sort((first, second) => {
    if (sortOrder === 'oldest-first') return first.day.toMillis() - second.day.toMillis();
    return second.day.toMillis() - first.day.toMillis();
  });

  return orderedGroups;
};

export const getTripBounds = (trips: TripSummary[]): Undefinable<TripBounds> => {
  if (!trips.length) return;

  const starts = trips.map((trip) => trip.start.toMillis());
  const finishes = trips.map((trip) => trip.finish.toMillis());

  return {
    end: DateTime.fromMillis(Math.max(...finishes)),
    start: DateTime.fromMillis(Math.min(...starts)),
  };
};

export const getTotalTripDuration = (groups: TripGroup[]): string => {
  const totalMinutes = groups.reduce((minutes, group) => {
    const groupMinutes = group.trips.reduce((groupTotal, trip) => {
      const tripMinutes = Math.max(Math.round(trip.finish.diff(trip.start, 'minutes').minutes), 0);
      return groupTotal + tripMinutes;
    }, 0);

    return minutes + groupMinutes;
  }, 0);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes}m`;
  if (!minutes) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

export const getTotalTripDistance = (groups: TripGroup[], tripMetricsByTripId: Record<string, TripMetrics>): string => {
  let totalDistanceKm = 0;
  let hasDistance = false;

  groups.forEach((group) => {
    group.trips.forEach((trip) => {
      const distanceKm = tripMetricsByTripId[trip.id]?.distanceKm;
      if (distanceKm === undefined) return;

      totalDistanceKm += distanceKm;
      hasDistance = true;
    });
  });

  return formatTotalDistanceLabel(hasDistance ? totalDistanceKm : undefined);
};

export const getTotalFuelConsumed = (groups: TripGroup[], tripMetricsByTripId: Record<string, TripMetrics>): string => {
  let totalFuelLiters = 0;
  let hasFuel = false;

  groups.forEach((group) => {
    group.trips.forEach((trip) => {
      const fuelLiters = tripMetricsByTripId[trip.id]?.fuelLiters;
      if (fuelLiters === undefined) return;

      totalFuelLiters += fuelLiters;
      hasFuel = true;
    });
  });

  return formatTotalFuelLabel(hasFuel ? totalFuelLiters : undefined);
};

